/*
 * A plain data table — the admin surfaces are three of these and nothing more (spec 12 §4.5). It
 * stays a real <table> with a caption and header cells, because that is what a screen reader can
 * navigate; a grid of divs cannot be repaired later.
 *
 * A column says what kind of cell it holds and the table draws it: `num` is a figure and is set
 * right-aligned in tabular digits so a column of counts lines up, `actions` is the row's buttons
 * and sits at the end edge. Everything else is text and reads from the start edge.
 */

import type { ReactNode } from "react"
import "./ui.css"

export type ColumnKind = "num" | "actions"

export interface Column<Row> {
	id: string
	/** Already translated. */
	header: string
	cell: (row: Row) => ReactNode
	/** Absent for text; `num` for a figure, `actions` for the row's controls. */
	kind?: ColumnKind
}

interface TableProps<Row> {
	/** Already translated; describes the table for someone who cannot see it. */
	caption: string
	columns: Column<Row>[]
	rows: Row[]
	rowKey: (row: Row) => string
}

const KIND_CLASS: Record<ColumnKind, string> = { num: "tb-num", actions: "tb-table__actions" }

export function Table<Row>({ caption, columns, rows, rowKey }: TableProps<Row>) {
	const classOf = (column: Column<Row>) =>
		column.kind === undefined ? undefined : KIND_CLASS[column.kind]
	return (
		<table className="tb-table">
			<caption>{caption}</caption>
			<thead>
				<tr>
					{columns.map((column) => (
						<th key={column.id} scope="col" className={classOf(column)}>
							{column.header}
						</th>
					))}
				</tr>
			</thead>
			<tbody>
				{rows.map((row) => (
					<tr key={rowKey(row)}>
						{columns.map((column) => (
							<td key={column.id} className={classOf(column)}>
								{column.cell(row)}
							</td>
						))}
					</tr>
				))}
			</tbody>
		</table>
	)
}
